import React, { useState, useEffect, useRef } from 'react';
import * as turf from "@turf/turf";
import axios from 'axios';
import Spinner from '../../../Spinner.js';
import { SearchBox } from '@mapbox/search-js-react';
import mapboxgl from 'mapbox-gl';
import locationIcon from '../../../../assets/icons/location-icon.png';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { drawStyle } from '../../../drawStyles.js';
import { useUserContext } from '../../../../contexts/UserContext.js';
import moment from 'moment-timezone';

export default function DrawPolygon(props) {
    const {
        is3D,
        setIs3D
    } = useUserContext();

    const {
        teamAreas,
        setTeamAreas,
        myAreas,
        setMyAreas,
        setPanel, 
        map,
        teams,
        getToken,
        plotAreas,
    } = props;

    const draw = useRef();
    const locationRef = useRef(null);

    const [polygon, setPolygon] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    const [addToTeam, setAddToTeam] = useState(false);
    const [selectedTeams, setSelectedTeams] = useState([])

    const [details, setDetails] = useState({
        name: '',
        description: 'No Description',
        team_id: null
    });

    const formatPolicyAndUniqueIDs = (input) => {
        if (input.includes("Policy ID") && input.includes("Unique ID")) {
          return input.split(',')
                      .map(part => part.trim())
                      .join('<br />');  // Use <br /> tags for HTML line breaks
        }
        return input;
      };
    
      const convertTo12HourFormat = (dateTimeString) => {
        const time = moment.tz(dateTimeString, "UTC");
      
        const localTime = time.tz(moment.tz.guess()).format('YYYY-MM-DD h:mm A');
      
        return localTime;
    }

    useEffect(() => {
        if (!draw.current) {
            draw.current = new MapboxDraw({
                displayControlsDefault: false,
                controls: { polygon: false },
                styles: drawStyle,
            });
        }
    
        if (map.current && !map.current._controls.find((control) => control === draw.current)) {
            map.current.addControl(draw.current);
            draw.current.changeMode('draw_polygon');
        }
    
        const handleUpdateShape = (e) => updateShape(e);
    
        map.current.on('draw.create', handleUpdateShape);
        map.current.on('draw.delete', handleUpdateShape);
        map.current.on('draw.update', handleUpdateShape);
    
        return () => {
            if (map.current) {
                map.current.off('draw.create', handleUpdateShape);
                map.current.off('draw.delete', handleUpdateShape);
                map.current.off('draw.update', handleUpdateShape);
    
                if (map.current._controls.find((control) => control === draw.current)) {
                    map.current.removeControl(draw.current);
                }

                if (locationRef.current) {
                    locationRef.current.remove();
                }
            }
        };
    }, [map]);
    
    const resetDrawingMode = () => {
        if (draw.current) {
            draw.current.deleteAll(); // Clear existing drawings
            draw.current.changeMode('draw_polygon'); // Reset to drawing mode
        }
    };

    const updateShape = (e) => {
        if (!draw.current) {
            console.error("MapboxDraw instance is not initialized.");
            return;
        }
    
        const data = draw.current.getAll();
        if (!data || !data.features) {
            console.warn("No features found in MapboxDraw.");
            setPolygon(null);
            return;
        }
    
        if (e.type === 'draw.delete') {
            setPolygon(null);
        } else if (data.features.length > 0) {
            const area = turf.area(data);
            const rounded_area = Math.round(area * 100) / 100;
            data.features[0].properties = { area: rounded_area };
            setPolygon(data);
        }
    };    

    const getLocation = () => {
        setError(false);
        setLocationLoading(true);
        if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const location = [position.coords.longitude, position.coords.latitude];
                 if (location) {
                    map.current.flyTo({ center: location, pitch: 0, zoom: 18 });

                    if (is3D) {
                        setIs3D(false);
                    }

                    if (locationRef.current) {
                        locationRef.current.remove();
                    }

                    const point = document.createElement('div');
                    point.className = 'map-current-location-marker';
                    const marker = new mapboxgl.Marker(point).setLngLat(location).addTo(map.current);
                    locationRef.current = marker;
                    setLocationLoading(false);
                }
            },
            (err) => {
                setLocationLoading(false);
                setError('Trouble getting your location. Please try again.');
            }
        );
        } else {
            setLocationLoading(false);
            setError('Trouble getting your location. Please try again.');
        }
    };
    
    const ringsToWKT = (rings) => {
        if(rings.length > 1) {
            const wkts = [];
            rings.forEach((poly) => {
                const list = [];
                poly.forEach((set) => {
                    list.push(set.toString().replace(',', '/'));
                });
                wkts.push(`POLYGON((${list.toString().replaceAll('/', ' ')}))`);
            });
            return wkts;
            
        } else {
            const list = [];
            rings[0].forEach((set) => {
                list.push(set.toString().replace(',', '/'));
            });
            return [`POLYGON((${list.toString().replaceAll('/', ' ')}))`];
        }
    }

    const WKTtoRings = (wkt) => {
        const rings = wkt.slice(9).slice(0, -2).split(',').map((coordinates) => {
            return coordinates.split(' ').filter((element) => element !== '').map((element) => parseFloat(element));
        });
        return rings;
    };

    const displayArea = (areaInMetersSquared) => {
        const conversionFactor = 0.000000386102;
        const areaInSquareMiles = areaInMetersSquared * conversionFactor;
        return Math.round(areaInSquareMiles * 100) / 100;
    };

    const convertMetersToSquareMiles = (wkt) => {
        const data = {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [WKTtoRings(wkt)]
                }
              }
            ]
        };
    
        const areaInMetersSquared = turf.area(data);
          
        const conversionFactor = 0.000000386102;
        const areaInSquareMiles = areaInMetersSquared * conversionFactor;
        return Math.round(areaInSquareMiles * 100) / 100;
      };
  
    
    const getBoundsURL = (rings) => {
        let min = [rings[0][0], rings[0][1]]
        let max = [rings[0][0], rings[0][1]]
 
        rings.forEach((pair) => {
            if(pair[0] < min[0]) min = [pair[0], min[1]]
            if(pair[1] < min[1]) min = [min[0], pair[1]]
            if(pair[0] > max[0]) max = [pair[0], max[1]]
            if(pair[1] > min[1]) max = [max[0], pair[1]] 
        });
            
        return `${min[0]},${min[1]},${max[0]},${max[1]}`
    }

    const getCentroid = (rings) => {
        let first = rings[0], last = rings[rings.length-1];

        if (first[0] != last[0] || first[1] != last[1]) 
            rings.push(first);

        let twicearea = 0, x=0, y=0, nrings = rings.length, p1, p2, f;

        for ( let i = 0, j = nrings-1 ; i < nrings ; j=i++ ) {
           p1 = rings[i]; p2 = rings[j];
           f = p1[0]*p2[1] - p2[0]*p1[1];
           twicearea += f;          
           x += ( p1[0] + p2[0] ) * f;
           y += ( p1[1] + p2[1] ) * f;
        }
        f = twicearea * 3;
        return [ x/f, y/f ];
    }

    const savePolygon = () => {
        setLoading(true);
        setError(false);
    
        if (!details.name) {
            setLoading(false);
            setError('Name field is required.');
            return;
        }

        if (!addToTeam) {
            getToken().then((token) => {
                axios.post(`https://firewatch-api.space-eyes.com/locations`, {
                    team_id: null,
                    id: null,
                    name: details.name,
                    description: details.description,
                    type: 'polygon',
                    value: null,
                    radius_miles: null,
                    geometry: ringsToWKT([polygon.features[0].geometry.coordinates[0]])[0],
                    location: null,
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }).then((res) => {
                    setLoading(false);

                    const rings = WKTtoRings(res.data.geometry);
   
                    setMyAreas([res.data, ...myAreas]);

                    plotAreas(map, [res.data, ...myAreas, ...(teamAreas || [])]);
                        
                    const layers = map.current.getStyle().layers.filter((layer) => layer.id.includes('Layer-areas-focused'));

                    if (layers.length > 0) {
                        layers.forEach((layer) => map.current.removeLayer(layer.id));
                    }
            
                    if (map.current.getStyle().sources[`Source-areas-focused-${res.data.id}`]) {
                        map.current.removeSource(`Source-areas-focused-${res.data.id}`);
                    }

                    const location = [res.data.longitude, res.data.latitude];

                    const existingPopups = document.getElementsByClassName('popup');
                    Array.from(existingPopups).forEach((popup) => popup.remove());

                    new mapboxgl.Popup({ offset: 25, maxWidth: '600px', className: 'popup' })
                    .setLngLat(location)
                    .setHTML(`
                        <div>
                        <div class='alert-popup-title'>${res.data.name}</div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Description:</div>
                            <div class='alert-popup-row-value'>${res.data.description ? formatPolicyAndUniqueIDs(res.data.description) : 'No Description'}</div>
                        </div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Type:</div>
                            <div class='alert-popup-row-value'>${res.data.type === 'point' ? 'POINT/ADDRESS' : 'POLYGON'}</div>
                        </div>
                       <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Area:</div>
                            <div class='alert-popup-row-value'>
                                ${convertMetersToSquareMiles(res.data.geometry)} square miles
                            </div>
                        </div>
                        ${res.data.value ?
                            `<div class='alert-popup-row'>
                                <div class='alert-popup-row-name'>Total Asset Value:</div>
                                <div class='alert-popup-row-value'>
                                $${Number(res.data.value).toLocaleString()}
                                </div>
                            </div>` : ' '}
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Created</div>
                            <div class='alert-popup-row-value'>${convertTo12HourFormat(res.data.created_timestamp)}</div>
                        </div>
                        </div>
                    `)
                    .addTo(map.current);
  
                    map.current.addSource(`Source-areas-focused-${res.data.id}`, {
                        'type': 'geojson',
                        'data': {
                            'type': 'Feature',
                            'geometry': {
                                'type': 'Polygon',
                                'coordinates': [rings]
                            }
                        }
                    });

                    map.current.addLayer({
                        'id': `Layer-areas-focused-${res.data.id}-fill`,
                        'type': 'fill',
                        'source': `Source-areas-focused-${res.data.id}`,
                        'layout': {},
                        'paint': {
                          'fill-color': 'rgba(240, 240, 240, 0.4)',
                        }
                    });
          
                    map.current.addLayer({
                        'id': `Layer-areas-focused-${res.data.id}-outline`,
                        'type': 'line',
                        'source': `Source-areas-focused-${res.data.id}`,
                        'layout': {},
                        'paint': {
                          'line-color': 'rgba(255, 255, 255, 1)',
                          'line-width': 2
                        }
                    });
        
                    const centroid = getCentroid(rings);
                    map.current.flyTo({ center: centroid, zoom: 15, pitch: 0, bearing: 0 });

                    const geometry = {
                        'type': 'Feature',
                        'geometry': {
                          'type': 'Polygon',
                          'coordinates': [rings]
                        }
                    }
            
                    map.current.fitBounds(turf.bbox(geometry), {
                      padding: 200,
                      pitch: 0
                    });

                    setPanel({
                        name: 'Empty', 
                        content: null
                    });
                }).catch((err) => {
                    console.error('Error adding areas:', err);
                    setLoading(false);
                    setError('Error creating Monitoring Area. Please try again.');
                }
                );
            }).catch((error) => console.log('error getting token', error));
        } else if (selectedTeams.length === 0) {
            setError('Please choose at least one team.');
            setLoading(false);
            return;
        } else if (selectedTeams.length > 0) {
            selectedTeams.forEach((team) => {
                getToken().then((token) => {
                    axios.post(`https://firewatch-api.space-eyes.com/locations`, {
                    team_id: team.id,
                    id: null,
                    name: details.name,
                    description: details.description,
                    type: 'polygon',
                    value: null,
                    radius_miles: null,
                    geometry: ringsToWKT([polygon.features[0].geometry.coordinates[0]])[0],
                    location: null,
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }).then((res) => {
                    setLoading(false);

                    const rings = WKTtoRings(res.data.geometry);
   
                    setTeamAreas([res.data, ...teamAreas]);

                    plotAreas(map, [res.data, ...(myAreas || []), ...teamAreas]);
                        
                    const layers = map.current.getStyle().layers.filter((layer) => layer.id.includes('Layer-areas-focused'));

                    if (layers.length > 0) {
                        layers.forEach((layer) => map.current.removeLayer(layer.id));
                    }
            
                    if (map.current.getStyle().sources[`Source-areas-focused-${res.data.id}`]) {
                        map.current.removeSource(`Source-areas-focused-${res.data.id}`);
                    }

                    const location = [res.data.longitude, res.data.latitude];

                    const existingPopups = document.getElementsByClassName('popup');
                    Array.from(existingPopups).forEach((popup) => popup.remove());
            
                    new mapboxgl.Popup({ offset: 25, maxWidth: '600px', className: 'popup' })
                    .setLngLat(location)
                    .setHTML(`
                        <div>
                        <div class='alert-popup-title'>${res.data.name}</div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Description:</div>
                            <div class='alert-popup-row-value'>${res.data.description ? formatPolicyAndUniqueIDs(res.data.description) : 'No Description'}</div>
                        </div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Type:</div>
                            <div class='alert-popup-row-value'>${res.data.type === 'point' ? 'POINT/ADDRESS' : 'POLYGON'}</div>
                        </div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Area:</div>
                            <div class='alert-popup-row-value'>
                            ${convertMetersToSquareMiles(res.data.geometry)} square miles
                            </div>
                        </div>
                        ${res.data.value ?
                            `<div class='alert-popup-row'>
                                <div class='alert-popup-row-name'>Total Asset Value:</div>
                                <div class='alert-popup-row-value'>
                                $${Number(res.data.value).toLocaleString()}
                                </div>
                            </div>` : ' '}
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Created</div>
                            <div class='alert-popup-row-value'>${convertTo12HourFormat(res.data.created_timestamp)}</div>
                        </div>
                        </div>
                    `)
                    .addTo(map.current);
  
                    map.current.addSource(`Source-areas-focused-${res.data.id}`, {
                        'type': 'geojson',
                        'data': {
                            'type': 'Feature',
                            'geometry': {
                                'type': 'Polygon',
                                'coordinates': [rings]
                            }
                        }
                    });

                    map.current.addLayer({
                        'id': `Layer-areas-focused-${res.data.id}-fill`,
                        'type': 'fill',
                        'source': `Source-areas-focused-${res.data.id}`,
                        'layout': {},
                        'paint': {
                          'fill-color': 'rgba(240, 240, 240, 0.4)',
                        }
                    });
          
                    map.current.addLayer({
                        'id': `Layer-areas-focused-${res.data.id}-outline`,
                        'type': 'line',
                        'source': `Source-areas-focused-${res.data.id}`,
                        'layout': {},
                        'paint': {
                          'line-color': 'rgba(255, 255, 255, 1)',
                          'line-width': 2
                        }
                    });
        
                    const centroid = getCentroid(rings);
                    map.current.flyTo({ center: centroid, zoom: 15, pitch: 0, bearing: 0 });

                    const geometry = {
                        'type': 'Feature',
                        'geometry': {
                          'type': 'Polygon',
                          'coordinates': [rings]
                        }
                    }
            
                    map.current.fitBounds(turf.bbox(geometry), {
                      padding: 200,
                      pitch: 0
                    });

                    setPanel({
                        name: 'Empty', 
                        content: null
                    });
                }).catch((err) => {
                    console.error('Error adding areas:', err);
                    setLoading(false);
                    setError('Error creating Monitoring Area. Please try again.');
                });
                }).catch((error) => {
                    console.log('error getting token', error);
                    setLoading(false);
                });
            })
        }
    
        resetDrawingMode();
    };

    const handleTeamCheck = (team, isChecked) => {
        if (isChecked) {
          setSelectedTeams(prevTeams => [...prevTeams, team]);
        } else {
          setSelectedTeams(prevTeams =>
            prevTeams.filter(item => item.id !== team.id)
          );
        }
    };

    return (
        polygon ?
        <div className='create-area-content-form'>
            <div className='create-area-content-form-subtitle'>Name</div>
            <input 
                className='create-area-content-form-input' 
                placeholder='Ex. Los Angeles, CA' 
                value={details.name} 
                onChange={(e) => setDetails({...details, name: e.target.value})}
            />
            <div className='create-area-content-form-subtitle'>Description</div>
            <textarea 
                className='create-area-content-form-description'  
                placeholder='No description' 
                value={details.description} 
                onChange={(e) => setDetails({...details, description: e.target.value})}
            />
            <div className='create-area-content-form-subtitle'>Do you want to add this area for yourself or for your team(s)?</div>
            <div className='create-area-content-form-radios'>
                <div className='create-area-content-form-radios-radio'>
                    <input
                    type="radio"
                    name="teamOption"
                    value="false"
                    checked={!addToTeam}
                    onChange={(e) => setAddToTeam(e.target.value === 'true')}
                    />
                    Only for me
                </div>
                <div className='create-area-content-form-radios-radio'>
                    <input
                        type="radio"
                        name="teamOption"
                        value="true"
                        checked={addToTeam}
                        onChange={(e) => setAddToTeam(e.target.value === 'true')}
                    />
                    Add to team
                </div>
            </div>
            {(addToTeam && teams) && 
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                {teams && teams.map(team => (
                    <div
                        key={team.id}
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '10px',
                            background: 'rgba(212, 212, 212, 0.58)',
                            padding: '0.7rem',
                            borderRadius: 10
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={selectedTeams.some(selected => selected.id === team.id)}
                            // The event returns the checkbox's checked state (true/false)
                            onChange={(e) => handleTeamCheck(team, e.target.checked)}
                            aria-label="teams-checkbox"
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            <span style={{ fontSize: '14px', color: 'black', fontWeight: 'bold' }}>
                            {team.name}
                            </span>
                            <span style={{ fontSize: '13px', color: 'black' }}>
                            {team.description}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
            }
            <div className='create-area-content-form-area'>
                <div className='create-area-content-form-area-title'>Calculated Area:</div>
                <div className='create-area-content-form-area-value'>{displayArea(polygon.features[0].properties.area).toLocaleString()} square miles</div>
            </div>
            {error && <div className='create-area-content-form-error'>{error}</div>}
            {loading ?
            <div style={{ paddingTop: '1rem' }}>
                <Spinner />
            </div>
            :
            <div className='create-area-content-form-btns'>
                <div 
                    className='create-area-content-form-btns-btn-save'
                    onClick={() => {
                        setPolygon(null);
                        setError(false);
                        resetDrawingMode();
                    }}
                    >
                    Discard
                </div>
                <div 
                    className='create-area-content-form-btns-btn-cancel'
                    onClick={savePolygon}
                    >
                    Create Area
                </div>
            </div>}
        </div>
        :
        <div className='create-area-content-instructions'>
            <div className='create-area-content-instructions-instruction'>
                <div className='create-area-content-instructions-instruction-title'>Step 1: Choose location</div>
                <div className='create-area-content-instructions-instruction-sub-title'>Option 1: Current Location</div>
                {locationLoading ?
                <div style={{ paddingTop: '1rem' }}>
                    <Spinner />
                </div>
                :
                <div 
                    className='create-area-content-instructions-instruction-current-location-btn'
                    onClick={getLocation}
                >
                    <img src={locationIcon} className='create-area-content-instructions-instruction-current-location-btn-icon'/>
                    <div 
                        className='create-area-content-instructions-instruction-current-location-btn-text'
                    >Use Current Location
                    </div>
                </div>}
                <div className='create-area-content-instructions-instruction-sub-title'>Option 2: Search a location</div>
                <SearchBox
                    accessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
                    value=''
                    onRetrieve={(e) => {
                        const location = e.features[0].geometry.coordinates;
                        map.current.flyTo({center: location, zoom: 11, pitch: 0, offset: [250, 0]});

                        if (is3D) {
                            setIs3D(false);
                        }
                    }}
                />
            </div>
            <div className='create-area-content-instructions-instruction'>
                <div className='create-area-content-instructions-instruction-title'>Step 2: Draw polygon on map</div>
                <div className='create-area-content-instructions-instruction-sub-title'>Click points on the map and connect the polygon.</div>
            </div>
        </div>
    )
}